// @eidollon/contracts/translation
//
// INTERNAL CONTRACT — services/api ↔ services/translation, and the public
// shape of POST /v1/translate/entity returned to the browser.
//
// Iron Rules:
//   * 4 — every LLM call goes through services/inference; this contract
//         doesn't carry model identifiers, only entity coordinates + lang.
//   * 11 — no new UI primitive; the response is a flat map the frontend hook
//          consumes directly.
//   * 12 — translation responses serve from precomputed `translations` rows
//          (zero LLM in the hot path); LLM only on TM/canonical miss.

import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  Quality — which layer produced the text
 * ────────────────────────────────────────────────────────────────────────── */

export const TranslationQuality = z.enum([
  'memory', // translation_memory hash-keyed exact hit
  'canonical', // translations entity-keyed canonical row
  'draft', // freshly produced by L4 LLM, not yet verified
  'source', // fallback — source text returned because translation failed
]);
export type TranslationQuality = z.infer<typeof TranslationQuality>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Entity types covered today. Tender + contract are the founder's mandate;
 *  the resolver registry in services/translation gates which kinds the
 *  /v1/translate/entity route accepts.
 * ────────────────────────────────────────────────────────────────────────── */

export const TranslatableEntityType = z.enum(['tender', 'contract']);
export type TranslatableEntityType = z.infer<typeof TranslatableEntityType>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Supported target languages — matches apps/terminal/src/lib/locale.ts
 *  SUPPORTED_LOCALES. Kept in this contract so services/api can reject
 *  unsupported codes before they reach the LLM (Iron Rule #12 budget cap).
 *  Add a code here, then mirror it in the frontend SUPPORTED_LOCALES; the
 *  typecheck failure is the cue if the two drift.
 * ────────────────────────────────────────────────────────────────────────── */

export const SupportedTargetLang = z.enum(['en', 'fr', 'zh', 'he', 'es', 'ar', 'de', 'pt', 'ja']);
export type SupportedTargetLang = z.infer<typeof SupportedTargetLang>;

/* ──────────────────────────────────────────────────────────────────────────
 *  POST /v1/translate/entity — the public surface the terminal consumes
 * ────────────────────────────────────────────────────────────────────────── */

export const EntityTranslateRequest = z
  .object({
    entity_type: TranslatableEntityType,
    entity_id: z.string().uuid(),
    /** Field names to translate (e.g. 'title', 'description'). Capped at 32
     *  to bound the inference fan-out per call. */
    fields: z.array(z.string().min(1).max(64)).min(1).max(32),
    /** BCP-47 target. Restricted to SupportedTargetLang — the LLM is never
     *  asked to translate to a code we don't render in the picker. Pair
     *  this with apps/terminal/src/lib/locale.ts when adding a language. */
    target_lang: SupportedTargetLang,
  })
  .strict();
export type EntityTranslateRequest = z.infer<typeof EntityTranslateRequest>;

export const EntityTranslateFieldResult = z
  .object({
    text: z.string(),
    language: z.string(), // resolved BCP-47 (= target_lang unless we fell back)
    quality: TranslationQuality,
  })
  .strict();
export type EntityTranslateFieldResult = z.infer<typeof EntityTranslateFieldResult>;

export const EntityTranslateResponse = z
  .object({
    entity_type: TranslatableEntityType,
    entity_id: z.string().uuid(),
    target_lang: z.string(),
    /** Field name → result. Missing keys mean the field didn't resolve and
     *  the frontend hook should fall back to the source value. */
    fields: z.record(z.string(), EntityTranslateFieldResult),
  })
  .strict();
export type EntityTranslateResponse = z.infer<typeof EntityTranslateResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Internal translate.ts options — services/api never touches this; only
 *  services/translation imports it. Kept here for shared typing.
 * ────────────────────────────────────────────────────────────────────────── */

export const TranslateOptions = z
  .object({
    entity: TranslatableEntityType,
    field: z.string(),
    source_text: z.string(),
    source_lang: z.string(),
    target_lang: z.string(),
    tenant_id: z.string().uuid(),
    classification: z.enum(['PUBLIC', 'INTERNAL', 'RESTRICTED']).default('PUBLIC'),
  })
  .strict();
export type TranslateOptions = z.infer<typeof TranslateOptions>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Glossary CRUD — admin-only via services/translation
 * ────────────────────────────────────────────────────────────────────────── */

// Strict allowlist matches the DB CHECK constraints in
// infra/migrations/0032_translations.sql. Prevents prompt injection through
// crafted glossary entries: no `{` `}` `<` `>` `\\`, no `{{` `}}`.
const SAFE_TERM = /^[^{}<>\\]+$/;
const NO_TEMPLATE_MARKERS = (s: string) => !/\{\{|\}\}/.test(s);

export const GlossaryEntry = z
  .object({
    source_term: z.string().min(1).max(128).regex(SAFE_TERM).refine(NO_TEMPLATE_MARKERS, {
      message: 'glossary source_term must not contain {{ or }} template markers',
    }),
    target_term: z.string().min(1).max(256).regex(SAFE_TERM).refine(NO_TEMPLATE_MARKERS, {
      message: 'glossary target_term must not contain {{ or }} template markers',
    }),
    lang: z.string().regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/),
    do_not_translate: z.boolean().default(false),
  })
  .strict();
export type GlossaryEntry = z.infer<typeof GlossaryEntry>;

export const GlossaryListResponse = z
  .object({
    entries: z.array(GlossaryEntry),
    glossary_version: z.number().int().nonnegative(),
  })
  .strict();
export type GlossaryListResponse = z.infer<typeof GlossaryListResponse>;
